import { selectUploadsForSetSummary, selectUploadsForSet, VideoState } from "./VideoSlice"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { get } from "nodash"
import { fileManagementApi } from "features/data-management/files/fileManagementApi"
import { compress } from "plugins/shared/compression"
import { VideoMetadata } from "@nova/slice-video"
import { createVideoJob } from "features/pubsub/entityActions/jobs/video"
import { stagedFiles } from "@nova/slice-multifile-upload"
import { useMemo } from "react"
import { parseTelemetryFile } from "./telemetry"
import { updateVideoFlight } from "features/pubsub/entityActions/flights/video"
import { selectActiveLayerId } from "panels/content/side-panel/slice.new"
import { selectActivatedFireId } from "features/map/control/selectors"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"

export const getFOVFromFocalLength = (focalLength: number) => {
    const sensorWidth = 36 / 1.25 // mm
    const sensorHeight = 24 / 1.25 // mm
    const fovX = 2 * Math.atan(sensorWidth / (2 * focalLength))
    const fovY = 2 * Math.atan(sensorHeight / (2 * focalLength))
    return [fovX, fovY]
}

const key = (organizationID: string, fireID: string, flightID: string, filename: string) => {
    return `public/org/${organizationID}/fire/${fireID}/flight/${flightID}/video/${filename}`
}

enum ProgressType {
    Processing = 0,
    Uploading = 1,
}

export const useProcessing = (setProgress?: (progresses: number[]) => void) => {
    const progresses = useMemo(() => [0, 0], [])
    const dispatch = useAppDispatch()
    const fireId = useAppSelector(selectActivatedFireId)!
    const workspaceId = useAppSelector(selectActiveWorkspaceId())!
    const activeLayerId = useAppSelector(selectActiveLayerId) || ""
    // flight summary has no activated flight id, so it needs to pass it in, when the flight view calls the hook it will have an activated flight id
    const summary = useAppSelector(selectUploadsForSetSummary(activeLayerId))
    const uploads = useAppSelector(selectUploadsForSet(activeLayerId))

    const uploadVideoData = async (srtFile?: File) => {
        if (!srtFile) return
        const metadataPath = key(workspaceId, fireId, activeLayerId, "metadata.srt")
        await fileManagementApi.putFile(srtFile, { target: metadataPath })
        return metadataPath
    }

    const uploadCompressedVideoData = async (metadata?: VideoMetadata) => {
        if (!metadata) return Promise.resolve(undefined)
        const data = await compress(metadata, "msg")
        const metadataPath = key(workspaceId, fireId, activeLayerId, "metadata.msg")
        await fileManagementApi.putFile(data, { target: metadataPath })
        return metadataPath
    }

    const updateProgress = (key: number) => (progress: number) => {
        progresses[key] = progress
        setProgress?.(progresses)
    }

    const uploadVideo = async (video: File) => {
        const videoPath = key(workspaceId, fireId, activeLayerId, video.name)
        const promise = fileManagementApi.putFile(video, {
            target: videoPath,
            onProgress: updateProgress(ProgressType.Uploading),
        })
        return { videoPath, videoUploadPromise: promise }
    }

    const processCallback = async () => {
        const videoFile = uploads?.find((upload) =>
            ((get(upload, "s3Key") as string | undefined) || "").toLowerCase().includes(".mp4")
        ) as VideoState
        const srtFile = uploads?.find(
            (upload) =>
                !((get(upload, "s3Key") as string | undefined) || "").toLowerCase().includes(".mp4")
        ) as VideoState | undefined
        const file = stagedFiles[videoFile.id]

        const uploadVideoPromise = uploadVideo(file)
        const { videoPath, videoUploadPromise } = await uploadVideoPromise
        let metadata
        try {
            metadata = await parseTelemetryFile(stagedFiles[srtFile?.id || ""])
        } catch (err) {
            console.error(err)
        }

        await videoUploadPromise

        const [srtPath, metadataPath] = await Promise.all([
            uploadVideoData(stagedFiles[srtFile?.id || ""]),
            uploadCompressedVideoData(metadata),
        ])
        updateProgress(ProgressType.Uploading)(1)
        dispatch(
            createVideoJob({
                data: {
                    srtPath,
                    metadataPath,
                    videoPath,
                },
                context: {
                    flightId: activeLayerId,
                    file: videoFile.s3Key!.split("/").at(-1)!,
                },
            })
        )
        if (metadata && metadata?.length) {
            const [lon, lat] = metadata[0]
            dispatch(
                updateVideoFlight({
                    id: activeLayerId,
                    updates: { startingPosition: { longitude: lon, latitude: lat } },
                })
            )
        }
    }

    const canProcessPlugin = (baseRequirements: boolean) =>
        baseRequirements && !!summary?.uploadCount && summary.uploadCount >= 1

    return {
        canProcessPlugin,
        processCallback,
        dependencies: [dispatch, uploads, fireId, activeLayerId],
    }
}
